'use client';

import React, { ReactElement, useContext, useState, useEffect, useCallback } from 'react';
import * as Sentry from '@sentry/react';
import { FirebaseError } from '@firebase/util';
import { Controller, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useNavigate } from 'react-router-dom';
import { AuthContext } from '../../../contexts/auth';
import { useAuthRedirect } from '../../../hooks/useAuthRedirect';
import { useUploadDispatch } from '../../../contexts/UploadContext';
import { usePageLayout } from '../../../hooks/usePageLayout';
import { useCompanyId } from '../../../hooks/useCompanyId';
import {
  Box,
  Button,
  Typography,
  Link,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableRow,
} from '@mui/material';
import CancelIcon from '@mui/icons-material/Cancel';
import { HelpOutlineOutlined, NavigateNext, Description, SwapHoriz } from '@mui/icons-material';
import {
  gray100,
  gray20,
  gray40,
  gray60,
  gray80,
  primaryButtonBackgroundColor,
  white,
} from '../../../constants/colors';
import { boldXxl, regularLg, regularSm, spHeaderTitle } from '../../../constants/location';
import { AUTO_CLASSIFY_HELP_LABEL } from '../../../constants/labels';
import MetaTitle from '../../utils/MetaTitle';
import SideBar from '../../uiParts/menu/SideBar';
import CustomLoading from '../../uiParts/form/CustomLoading';
import CustomSnackbar from '../../uiParts/form/CustomSnackbar';
import useSnackbar from '../../../hooks/useSnackbar';
import FileUploader from '../../uiParts/form/FileUploader';
import OutputTypeSelector from '../../uiParts/form/OutputTypeSelector';
import DeleteModal from '../../uiParts/modal/DeleteModal';
import TutorialModal from '../../uiParts/modal/TutorialModal';
import TutorialCard from '../../uiParts/card/TutorialCard';
import UsageStatusCard from '../../uiParts/card/UsageStatusCard';
import FaqCard from '../../uiParts/card/FaqCard';
import ReferralInvitationCard from '../../uiParts/card/ReferralInvitationCard';
import { OcrFileNameStatus, OcrStatusIcon } from '../../uiParts/parts/OcrStatusFileInfo';
import SubPatternChip from '../../uiParts/SubPatternChip';
import ReviewChip from '../../uiParts/ReviewChip';
import { isNeedsReviewResult } from '../../../utils/pattern/needsReviewFilter';
import { classificationUploadSchema } from '../../../validation/input';
import { type InputUploadType } from '../../../types/input';
import { UserType } from '../../../types/user';
import {
  fileProcessingType,
  ocrStatus,
  companyPlan,
  PATTERN_ID_OTHER,
  FREE_OCR_PATTERN_ID,
  GEMINI_MODEL_DEFAULT,
} from '../../../constants/enum';
import PatternSelectModal from '../../uiParts/modal/PatternSelectModal';
import { handleReOcrPatternResult } from '../../../api/firebase/function/handleReOcrPatternResult';
import { RecentResultNavItem } from '../../../types/result';

import { getUserRecentResults } from '../../../api/firebase/firestore/result/read';
import { deleteResult } from '../../../api/firebase/firestore/result/delete';
import { editResult } from '../../../api/firebase/firestore/result/update';
import { getUser } from '../../../api/firebase/firestore/user/read';
import { getPattern } from '../../../api/firebase/firestore/pattern/read';
import {
  getCompanyForAccount,
  getMonthlyUsageCountByUserId,
} from '../../../api/firebase/firestore/company/read';
import { resolveDefaultAssignee } from '../../../api/firebase/firestore/company/resolveDefaultAssignee';
import {
  getCreditRateByPlan,
  calculateOcrLimitInfo,
  getDisplayUploadLimit,
  getUserMonthlyOcrLimit,
  getUserMonthlyOcrLimitMessage,
  getOcrLimitOverMessage,
  getOcrCreditUsageMessage,
} from '../../../utils/userUsageLimit';
import {
  HELP_BASE_URL,
  HELP_FAQ_ITEM_SETTING_TIPS,
  HELP_FAQ_LAYOUT_NOTATION,
  HELP_FAQ_OPERATION_STEPS,
  HELP_FAQ_AUTO_CLASSIFICATION,
  TUTORIAL_VIDEO_URL,
} from '../../../constants/externalUrls';
import {
  getPatternCountByCompanyId,
  hasDeletedPatternsByCompanyId,
} from '../../../api/firebase/firestore/pattern/read';
import { type DocumentData, doc, onSnapshot } from 'firebase/firestore';
import { db } from '../../../api/firebase/config';
import { isPdfFile, calculateTotalPageCount, getPdfPageCount } from '../../../utils/pdfToImage';
import { useTotalPageCount } from '../../../hooks/useTotalPageCount';
import { useUserMonthlyOcrLimit } from '../../../hooks/useUserMonthlyOcrLimit';
import { isOperatorStaff } from '../../../utils/permission';
import { convertToUserFriendlyMessage } from '../../../utils/errorHandler';
import { executeProvisionalOcrUsage } from '../../../utils/provisionalOcrUsage';
import OcrLimitDialog from '../../uiParts/modal/OcrLimitDialog';

// 1回のアップロードにおける合計ページ数の上限（サーバー負荷軽減のため）
const MAX_TOTAL_PAGES = 200;
// singleResult（1ファイル=1結果）の場合のPDFページ数上限
const MAX_PAGES_PER_FILE = 20;
// ローディングがこの時間（ミリ秒）以上続いた場合はタイムアウトとみなし、
// 強制解除してエラーを表示する（何らかの原因でloadingが止まらない場合のユーザー救済措置）
const LOADING_TIMEOUT_MS = 10000;

const Home = (): ReactElement => {
  const [dataLoading, setDataLoading] = useState<boolean>(true);
  const { errorText, infoText, snackbarOpen, setErrorText, setInfoText, setSnackbarOpen } =
    useSnackbar();
  const [recentResults, setRecentResults] = useState<DocumentData[]>([]);
  const [processingTypeValue, setProcessingTypeValue] = useState<number>(
    fileProcessingType.pagePerResult,
  );
  const [deleteModalOpen, setDeleteModalOpen] = useState<boolean>(false);
  const [deleteTargetId, _setDeleteTargetId] = useState<string>('');

  const [limitDialogOpen, setLimitDialogOpen] = useState<boolean>(false);
  const [reclassifyModalOpen, setReclassifyModalOpen] = useState<boolean>(false);
  const [reclassifyTargetResultId, setReclassifyTargetResultId] = useState<string>('');
  const [reclassifyTargetPatternId, setReclassifyTargetPatternId] = useState<string>('');
  const [reclassifyLoading, setReclassifyLoading] = useState<boolean>(false);
  // pagePerResultモード用: ファイルごとのページ数キャッシュ（ファイル名 → ページ数）
  const [pdfPageCountCache, setPdfPageCountCache] = useState<Map<string, number>>(new Map());
  const navigate = useNavigate();
  // 未認証ならログイン画面へ退避する（元URLは ?redirect= で引き継ぐ。#3203）
  useAuthRedirect();
  const { user, company, isLoading } = useContext(AuthContext);
  const { startUpload } = useUploadDispatch();
  const { sideBarCollapsed, toggleSidebar, styles: layoutStyles } = usePageLayout();
  const { companyId: effectiveCompanyId, isProxyMode, operatorId } = useCompanyId();

  // ユーザーのOCR利用枚数管理
  const [firestoreUser, setFirestoreUser] = useState<UserType | null>(null);
  const [baseRemainingCount, setBaseRemainingCount] = useState<number | null>(null);
  const [baseRemainingJpy, setBaseRemainingJpy] = useState<number | null>(null);

  // チュートリアル関連
  const [showTutorial, setShowTutorial] = useState<boolean | null>(null); // null = loading
  const [tutorialModalOpen, setTutorialModalOpen] = useState<boolean>(false);

  // 今月の利用状況
  const [monthlyUsageCount, setMonthlyUsageCount] = useState<number>(0);

  // ローディングがタイムアウトしたか（trueになるとloadingを強制解除して本体UIを操作可能にする）
  const [loadingTimedOut, setLoadingTimedOut] = useState<boolean>(false);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    trigger,
    setError,
    formState: { errors },
  } = useForm<InputUploadType>({
    resolver: yupResolver(classificationUploadSchema),
    mode: 'onChange',
    defaultValues: {
      images: [],
    },
  });

  // 合計ページ数を計算（PDFはページ数分、画像は1ページとしてカウント）
  const currentImages = watch('images');
  const { totalPageCount, isCalculating } = useTotalPageCount(currentImages);

  // 個人別 月間OCR上限をリアルタイム監視（#3133）
  // 会社枠と同じくファイル選択時点でインラインのバリデーションエラーを出すために事前購読する。
  const { getLimitMessage: getUserLimitMessage } = useUserMonthlyOcrLimit(
    user?.uid,
    effectiveCompanyId ?? user?.company_id,
    isProxyMode,
  );

  // Firestoreからユーザー情報を取得
  useEffect(() => {
    const fetchUserData = async (): Promise<void> => {
      if (user) {
        const userData = await getUser(user.uid);
        if (userData) {
          setFirestoreUser(userData as UserType);
        }
      }
    };

    if (user) {
      fetchUserData();
    }
  }, [user]);

  // 企業情報が取得できない場合、ローディングを解除してエラー表示
  // 代理モード時はcompanyがonSnapshot経由で非同期に届くため、isProxyModeの場合はチェックしない
  useEffect(() => {
    if (!isLoading && user && !company && !isProxyMode) {
      setShowTutorial(false);
      setDataLoading(false);
      setErrorText('企業情報の取得に失敗しました。管理者にお問い合わせください。');
      setSnackbarOpen(true);
    }
  }, [isLoading, user, company, isProxyMode]);

  // チュートリアル表示判定とパターン数を取得
  // 代理モード中は対象企業を見る（#3203）。ログインユーザー自身の company_id を見ていると、
  // URL の ?proxy= から代理モードが後から確立しても運営企業のパターン数で判定し続けてしまう。
  // 運営スタッフは company_id を持たない場合があり、そのときは showTutorial が null のままとなり
  // ローディングが解除されない（10秒のタイムアウト救済に頼ることになる）。
  useEffect(() => {
    const companyIdForTutorial = effectiveCompanyId ?? firestoreUser?.company_id ?? null;

    const fetchData = async (): Promise<void> => {
      if (companyIdForTutorial) {
        try {
          // パターン数を取得してチュートリアル表示判定（1件以上でファイルアップロード表示）
          const count = await getPatternCountByCompanyId(companyIdForTutorial);
          const shouldShowTutorial = count < 1;
          setShowTutorial(shouldShowTutorial);

          // パターン0件かつ過去にパターンを作成したことがない場合のみチュートリアルモーダルを自動表示
          // （削除済みパターンがあれば、パターン機能の利用経験あり → 表示しない）
          if (shouldShowTutorial) {
            const hasDeleted = await hasDeletedPatternsByCompanyId(companyIdForTutorial);
            if (!hasDeleted) {
              setTutorialModalOpen(true);
            }
          }
        } catch (error) {
          // eslint-disable-next-line no-console
          console.error('[Home] Error fetching data:', error);
        }
      } else {
        // 対象企業が決まらない場合もローディングを解除する（#3203）。
        // null のままだとチュートリアル判定待ちでスピナーが残り続ける。
        setShowTutorial(false);
      }
    };

    if (firestoreUser) {
      fetchData();
    }
  }, [firestoreUser, effectiveCompanyId]);

  // 残りアップロード可能枚数と今月の利用枚数をリアルタイム監視（companiesドキュメントを使用）
  // 代理モード中は対象企業を監視する（#3203）。AuthContext.company は代理確立後に
  // 非同期で差し替わるため、それを待つと運営企業の残枚数が一時的に表示されてしまう。
  useEffect(() => {
    const companyIdToWatch = effectiveCompanyId ?? company?.company_id ?? null;
    if (!companyIdToWatch) return;

    const companyDocRef = doc(db, 'companies', companyIdToWatch);
    const unsubscribe = onSnapshot(
      companyDocRef,
      snapshot => {
        if (snapshot.exists()) {
          const data = snapshot.data();
          const currentMonthOcrCount = data.current_month_ocr_count ?? 0;
          const currentMonthOcrCost = data.current_month_ocr_cost ?? 0;
          // フォールバックは監視対象のドキュメントに値が無い場合のみ使う。
          // 代理確立直後は company がまだ運営企業を指しているため、
          // 他社の値で残枚数を算出しないよう既定値へ寄せる。
          const ocrMonthlyBudget = (data.ocr_monthly_budget as number | undefined) ?? 0;
          const creditRate = getCreditRateByPlan(
            (data.plan as number | undefined) ?? companyPlan.free,
          );
          const baseRemaining = Math.floor((ocrMonthlyBudget - currentMonthOcrCost) / creditRate);
          setBaseRemainingCount(Math.max(0, baseRemaining));
          setBaseRemainingJpy(Math.max(0, ocrMonthlyBudget - currentMonthOcrCost));
          setMonthlyUsageCount(currentMonthOcrCount);
        }
      },
      error => {
        // ログアウト直後にリスナーが permission-denied を受け取るのは正常な動作
        if (error instanceof FirebaseError && error.code === 'permission-denied') {
          return;
        }
        Sentry.captureException(error);
      },
    );

    return () => {
      unsubscribe();
    };
  }, [company, effectiveCompanyId]);

  // 最新結果の取得でエラーが発生した場合の共通ハンドラ
  // ホーム画面は最近の結果のみの軽量なページであり、本来エラーは起きない想定。
  // エラー時はローディングを解除してエラーを表示し、ユーザーが操作不能にならないようにする。
  const handleResultsError = useCallback((): void => {
    setDataLoading(false);
    setErrorText('データの取得に失敗しました。運営にお問い合わせください。');
    setSnackbarOpen(true);
  }, [setErrorText, setSnackbarOpen]);

  // ユーザーの最新結果30件をリアルタイム取得
  // 代理モード時は対象企業のファイルのみ表示
  useEffect(() => {
    if (!user) return;

    let unsubscribe: (() => void) | undefined;

    const setupListener = async (): Promise<void> => {
      setDataLoading(true);
      // 代理モード時は companyId でフィルタ、通常時は userId でフィルタ
      if (isProxyMode && effectiveCompanyId) {
        unsubscribe = await getUserRecentResults(
          '',
          results => {
            setRecentResults(results);
            setDataLoading(false);
          },
          effectiveCompanyId,
          handleResultsError,
        );
      } else {
        unsubscribe = await getUserRecentResults(
          user.uid,
          results => {
            setRecentResults(results);
            setDataLoading(false);
          },
          company?.company_id,
          handleResultsError,
        );
      }
    };

    setupListener();

    return () => {
      if (unsubscribe) {
        unsubscribe();
      }
    };
  }, [user, isProxyMode, effectiveCompanyId, handleResultsError]);

  // ローディング救済措置（保険）: データ読み込み（dataLoading / showTutorial）が
  // LOADING_TIMEOUT_MS 以上続いた場合、強制的にloadingを解除してエラーを表示する。
  // 本来はエラー発生時に handleResultsError 等で即座にloadingを解除するが、
  // エラーすら発生せずコールバックが呼ばれないハング状態でも、ユーザーが
  // 本体UI（サイドバー含む）を操作不能にならないようにするための最後の保険。
  useEffect(() => {
    const isDataLoading = dataLoading || showTutorial === null;
    // 読み込み完了済み、または既にタイムアウト済みなら何もしない
    if (!isDataLoading || loadingTimedOut) return;

    const timerId = setTimeout(() => {
      setLoadingTimedOut(true);
      setErrorText('データの取得に失敗しました。運営にお問い合わせください。');
      setSnackbarOpen(true);
    }, LOADING_TIMEOUT_MS);

    return () => {
      clearTimeout(timerId);
    };
  }, [dataLoading, showTutorial, loadingTimedOut, setErrorText, setSnackbarOpen]);

  const styles = {
    buttonLarge: {
      height: '44px',
      padding: '16px',
      '& .MuiButton-icon': {
        marginRight: '4px',
      },
    },
    mainContainer: {
      display: 'grid',
      gridTemplateColumns: '1fr 360px',
      columnGap: '24px',
      rowGap: '12px',
      width: '100%',
      marginTop: '12px',
      marginBottom: '24px',
      '@media (max-width: 1200px)': {
        gridTemplateColumns: '1fr',
      },
    },
    leftColumn: {
      display: 'flex',
      flexDirection: 'column',
      minWidth: 0,
    },
    rightColumn: {
      display: 'flex',
      flexDirection: 'column',
      gap: '12px',
      minWidth: 0,
      alignSelf: 'start',
    },
    container: {
      width: '100%',
      display: 'flex',
      flexDirection: 'column',
      padding: '24px',
      backgroundColor: white,
      boxSizing: 'border-box',
      borderRadius: '8px',
      border: `1px solid ${gray40}`,
      '@media (max-width: 960px)': {
        padding: '16px',
      },
    },
    subSectionTitle: {
      ...regularLg,
      color: gray100,
      marginBottom: '24px',
      textAlign: 'left',
    },
    tableWrapper: {
      overflowX: 'auto',
    },
    table: {
      minWidth: '1260px',
    },
    tableHeadRow: {
      height: '48px',
    },
    tableHeadText: {
      ...regularSm,
      color: gray80,
      padding: '0 16px',
    },
    tableDataRow: {
      height: '48px',
      '&:hover': {
        backgroundColor: gray20,
        cursor: 'pointer',
      },
    },
    tableDataText: {
      ...regularSm,
      color: gray100,
      padding: '0 16px',
    },
    tableCellButton: {
      padding: 0,
    },
    uploadSubmitButton: {
      height: '44px',
      width: '100%',
      maxWidth: '320px',
      margin: '0 auto',
      display: 'block',
      ...primaryButtonBackgroundColor,
      '&.Mui-disabled': {
        backgroundColor: gray40,
        '& .MuiTypography-root': {
          color: gray80,
        },
      },
      '@media (max-width: 960px)': {
        width: '100%',
        maxWidth: 'none',
      },
    },
    uploadSubmitButtonText: {
      fontSize: '16px',
      fontWeight: 700,
    },
    tableContainer: {
      width: 'auto',
      maxWidth: '100%',
      overflowX: 'auto',
      padding: '0px 0px 0px 10px',
      border: `1px solid ${gray40}`,
      borderRadius: '2px',
    },
    TableCell: {
      color: gray100,
      fontSize: '14px',
      fontWeight: 400,
      padding: '8px 10px 8px 0',
      whiteSpace: 'nowrap',
      textOverflow: 'ellipsis',
      overflow: 'hidden',
      minWidth: '35px',
      maxWidth: '140px',
      '@media screen and (max-width:960px)': {
        maxWidth: '130px',
      },
    },
    TableRow: {
      '&:last-child td': {
        borderBottom: 'none',
      },
    },
    cancelButton: {
      color: gray60,
      height: '24px',
      width: '24px',
      minWidth: '0',
      padding: '0',
      '&:hover': {
        backgroundColor: 'transparent',
        boxShadow: 'none',
        transform: 'none',
      },
    },
    deleteButton: {
      height: '32px',
      fontSize: '14px',
    },
    tableCellAvatar: {
      alignItems: 'center',
      display: 'flex',
      gap: '8px',
      height: '48px',
      padding: '0 16px',
    },
    avatarWrapper: {
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
    },
    avatar: {
      width: 32,
      height: 32,
    },
    showFileTitleText: {
      ...regularSm,
      color: gray100,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      maxWidth: '150px',
      display: 'inline-block',
      verticalAlign: 'middle',
    },
    listWrapper: {
      display: 'flex',
      flexDirection: 'column',
      width: '100%',
      overflowX: 'auto',
    },
    listItem: {
      display: 'flex',
      alignItems: 'center',
      padding: '12px 16px 12px 8px',
      borderBottom: `1px solid ${gray20}`,
      transition: 'background-color 0.2s',
      minWidth: '600px',
      '&:last-child': {
        borderBottom: 'none',
      },
    },
    listItemIconContainer: {
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      width: '40px',
      height: '40px',
      flexShrink: 0,
    },
    listItemMargin: {
      display: 'flex',
      alignItems: 'center',
      flex: 1,
      minWidth: 0,
      paddingLeft: '8px',
    },
    listItemBadgeContainer: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      alignItems: 'flex-start',
      flex: '0 0 180px',
      minWidth: 0,
      overflow: 'hidden',
      '@media (max-width: 960px)': {
        flex: '0 0 120px',
      },
    },
    listItemBadge: {
      display: 'inline-flex',
      alignItems: 'center',
      padding: '5px 12px',
      backgroundColor: '#eaeff5',
      borderRadius: '4px',
      height: 'fit-content',
      maxWidth: '100%',
    },
    listItemBadgeText: {
      fontSize: '14px',
      fontWeight: 400,
      lineHeight: '16px',
      color: gray100,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    listItemBadgeTextOnly: {
      fontSize: '14px',
      fontWeight: 400,
      color: gray80,
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
    listItemDateTime: {
      display: 'flex',
      flexDirection: 'column',
      justifyContent: 'center',
      fontSize: '16px',
      fontWeight: 400,
      lineHeight: '20px',
      color: gray100,
      height: '40px',
      flex: '0 0 160px',
      '@media (max-width: 960px)': {
        flex: '0 0 120px',
      },
    },
    listItemArrow: {
      fontSize: '24px',
      color: gray60,
      flexShrink: 0,
    },
  };

  // ファイルサイズのフォーマット
  const formatFileSize = (size: number): string => {
    if (size >= 1_000_000_000) return `${(size / 1_000_000_000).toFixed(2)} GB`;
    if (size >= 1_000_000) return `${(size / 1_000_000).toFixed(2)} MB`;
    if (size >= 1_000) return `${(size / 1_000).toFixed(2)} KB`;
    return `${size} B`;
  };

  // 上限枚数チェックのエラーメッセージを生成
  const getLimitErrorMessage = (
    totalPageCount: number,
    limitInfo: ReturnType<typeof calculateOcrLimitInfo>,
  ): string | null => {
    // 個人別 月間OCR上限（#3133）。会社枠より先に判定し、超過していれば理由を明示する。
    const userLimitMessage = getUserLimitMessage(totalPageCount);
    if (userLimitMessage) return userLimitMessage;

    if (baseRemainingCount === null || !company) return null;
    return getOcrLimitOverMessage(totalPageCount, limitInfo);
  };

  const handleFilesDeleted = async (index: number): Promise<void> => {
    const currentImages = watch('images');
    const newImages = currentImages.filter((_, i) => i !== index);
    setValue('images', newImages);

    // バリデーションを再実行してエラー表示を更新
    const isValid = await trigger('images');

    // 上限枚数チェック（月間枠 + OCRクレジット分）- ページ数ベース
    const totalPageCount = await calculateTotalPageCount(newImages);
    const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
    const limitError = getLimitErrorMessage(totalPageCount, limitInfo);

    if (isValid && limitError) {
      // yupバリデーションは通ったが上限超過の場合
      setError('images', { type: 'manual', message: limitError });
    }
    // yupバリデーションエラーがある場合はtriggerが既にエラーを設定済み
    // 両方OKの場合もtriggerがエラーをクリア済み
  };

  // 出力方式変更時のバリデーション再実行
  const handleProcessingTypeChange = async (
    newValue: number,
    currentFiles: File[],
  ): Promise<void> => {
    if (newValue === fileProcessingType.pagePerResult) {
      // pagePerResultに切り替え時: ページ数キャッシュを更新
      const newCache = new Map(pdfPageCountCache);
      for (const file of currentFiles) {
        if (isPdfFile(file) && !newCache.has(file.name)) {
          const count = await getPdfPageCount(file);
          newCache.set(file.name, count);
        }
      }
      setPdfPageCountCache(newCache);
    } else if (newValue === fileProcessingType.singleResult) {
      // singleResult時のページ数上限チェック
      for (const file of currentFiles) {
        if (isPdfFile(file)) {
          const pageCount = await getPdfPageCount(file);
          if (pageCount > MAX_PAGES_PER_FILE) {
            setError('images', {
              type: 'manual',
              message: `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
            });
            return;
          }
        }
      }
    }

    // バリデーションを再実行
    await trigger('images');
  };

  // 削除処理
  const handleDeleteResult = async (): Promise<void> => {
    try {
      await deleteResult(deleteTargetId);
      setDeleteModalOpen(false);
      setInfoText('ファイルを削除しました。');
      setSnackbarOpen(true);
    } catch (e) {
      setInfoText('');
      setErrorText('削除に失敗しました。');
      setSnackbarOpen(true);
    }
  };

  // アップロード処理（バックグラウンド実行: 画面遷移可能）
  const onSubmit = async (data: InputUploadType): Promise<void> => {
    if (!data.images || data.images.length === 0 || !user) {
      return;
    }

    setErrorText('');

    try {
      // 合計ページ数の上限チェック（サーバー負荷軽減のため）
      const uploadPageCountForLimit = await calculateTotalPageCount(data.images);
      if (uploadPageCountForLimit > MAX_TOTAL_PAGES) {
        setErrorText(
          `合計ページ数が上限を超えています。（${uploadPageCountForLimit}ページ / 上限${MAX_TOTAL_PAGES}ページ）`,
        );
        setSnackbarOpen(true);
        return;
      }

      // singleResult（1ファイル=1結果）の場合、1ファイルあたりのページ数上限をチェック
      if (processingTypeValue === fileProcessingType.singleResult) {
        for (const file of data.images) {
          if (isPdfFile(file)) {
            const pageCount = await getPdfPageCount(file);
            if (pageCount > MAX_PAGES_PER_FILE) {
              setErrorText(
                `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
              );
              setSnackbarOpen(true);
              return;
            }
          }
        }
      }

      // 月間OCR上限チェック（送信前）。代理モードは対象外（カウント・クレジット消費なし）。
      // パターンOCR（PatternAiOcr）と挙動を揃え、超過時はスナックバーで理由を明示して中止する（#3097）。
      if (!isProxyMode) {
        // 企業プランの月間枠 + OCRクレジット分のチェック
        if (baseRemainingCount !== null && company) {
          const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
          const overMessage = getOcrLimitOverMessage(uploadPageCountForLimit, limitInfo);
          if (overMessage) {
            setErrorText(overMessage);
            setSnackbarOpen(true);
            return;
          }
        }

        // 個人別 月間OCR上限チェック（#2804 Phase4）
        // 選択時にもインライン表示するようになった（#3133）が、
        // 表示後に他端末で消費された場合に備え最終防波堤として最新値で再判定する。
        const companyIdForUsage = effectiveCompanyId ?? user.company_id;
        const execUserDoc = await getUser(user.uid);
        const monthlyLimit = getUserMonthlyOcrLimit((execUserDoc as UserType | undefined) ?? null);
        if (monthlyLimit !== null && companyIdForUsage) {
          const usedPages = await getMonthlyUsageCountByUserId(companyIdForUsage, user.uid);
          const userLimitMessage = getUserMonthlyOcrLimitMessage(
            monthlyLimit,
            usedPages,
            uploadPageCountForLimit,
          );
          if (userLimitMessage) {
            setErrorText(userLimitMessage);
            setSnackbarOpen(true);
            return;
          }
        }
      }

      // AI学習許可フラグを取得
      let allowAiLearning = true;
      if (user.company_id) {
        try {
          const companyData = await getCompanyForAccount(user.company_id);
          if (companyData) {
            allowAiLearning = companyData.allow_ai_learning;
          }
        } catch {
          // エラー時もデフォルト値(true)を使用して処理継続
        }
      }

      // ページ展開済みファイル名リストを生成（パネル表示用）
      const pageFileNames: string[] = [];
      for (const file of data.images) {
        if (isPdfFile(file) && processingTypeValue === fileProcessingType.pagePerResult) {
          const count = await getPdfPageCount(file);
          const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
          const ext = file.name.match(/\.[^/.]+$/)?.[0] ?? '';
          for (let i = 1; i <= count; i++) {
            pageFileNames.push(count > 1 ? `${nameWithoutExt}_${i}${ext}` : file.name);
          }
        } else {
          pageFileNames.push(file.name);
        }
      }

      // 担当者デフォルト値（#3061）。通常=本人 / 代理=企業担当者（取得不可なら未設定）
      const defaultAssignee = await resolveDefaultAssignee({
        isProxyMode,
        companyId: effectiveCompanyId,
        user: { uid: user.uid, name: user.name, iconUrl: user.icon },
      });

      // 実際に生成される結果数（#3131）
      // pagePerResult: 1ページ=1結果なので合計ページ数 / singleResult: 1ファイル=1結果なのでファイル数
      const resultCount =
        processingTypeValue === fileProcessingType.pagePerResult
          ? uploadPageCountForLimit
          : data.images.length;

      // バックグラウンドでアップロード開始
      startUpload({
        type: 'classification',
        files: data.images,
        originalFiles: data.images,
        totalPageCount: resultCount,
        pageFileNames,
        userId: user.uid,
        userName: user.name || '',
        userIconUrl: user.icon,
        assigneeId: defaultAssignee.uid,
        assigneeName: defaultAssignee.name,
        assigneeIconUrl: defaultAssignee.iconUrl,
        processingType: processingTypeValue,
        geminiModel: GEMINI_MODEL_DEFAULT,
        companyId: effectiveCompanyId || user.company_id || '',
        allowAiLearning,
        isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
        company,
        baseRemainingCount,
        baseRemainingJpy,
      });

      // ファイルリストをクリア（結果はFirestoreリスナーで自動更新）
      setValue('images', []);

      // 処理結果を通知
      setInfoText(
        `${resultCount}件のファイルをアップロードしました。バックグラウンドで仕分け処理を実行中です。`,
      );
      setSnackbarOpen(true);
    } catch (error) {
      setErrorText(convertToUserFriendlyMessage(error));
      setSnackbarOpen(true);
    }
  };

  // ファイル選択ボタン押下時のチェック
  const handleFileUploadButtonClick = (): boolean => {
    // 月間枠超過かつOCRクレジットがない場合はダイアログ表示
    if (
      baseRemainingCount !== null &&
      baseRemainingCount <= 0 &&
      (!company || company.ocr_credit_balance <= 0)
    ) {
      setLimitDialogOpen(true);
      return false;
    }
    return true;
  };

  // ファイル選択処理
  const handleFilesSelected = async (
    files: FileList | null,
    onChange: (value: File[]) => void,
  ): Promise<void> => {
    if (!files || files.length === 0) {
      return;
    }

    const filesArray = Array.from(files);
    const currentFiles = watch('images') || [];

    const newFiles: File[] = [...currentFiles, ...filesArray];

    // pagePerResultモードのPDFはページ数をキャッシュ（リスト展開表示用）
    if (processingTypeValue === fileProcessingType.pagePerResult) {
      const newCache = new Map(pdfPageCountCache);
      for (const file of filesArray) {
        if (isPdfFile(file) && !newCache.has(file.name)) {
          const count = await getPdfPageCount(file);
          newCache.set(file.name, count);
        }
      }
      setPdfPageCountCache(newCache);
    }

    // ファイルを追加
    onChange(newFiles);

    // ファイル追加後に即座にバリデーションを実行
    setTimeout(async () => {
      // バリデーションを実行
      const isValid = await trigger('images');

      // 合計ページ数の上限チェック
      const totalPages = await calculateTotalPageCount(newFiles);
      if (isValid && totalPages > MAX_TOTAL_PAGES) {
        setError('images', {
          type: 'manual',
          message: `合計ページ数が上限を超えています。（${totalPages}ページ / 上限${MAX_TOTAL_PAGES}ページ）`,
        });
        return;
      }

      // singleResult時の1ファイルあたりPDFページ数上限チェック
      if (isValid && processingTypeValue === fileProcessingType.singleResult) {
        for (const file of newFiles) {
          if (isPdfFile(file)) {
            const pageCount = await getPdfPageCount(file);
            if (pageCount > MAX_PAGES_PER_FILE) {
              setError('images', {
                type: 'manual',
                message: `「${file.name}」のページ数が上限を超えています（${pageCount}ページ / 上限${MAX_PAGES_PER_FILE}ページ）。`,
              });
              return;
            }
          }
        }
      }

      // 上限枚数チェック（月間枠 + OCRクレジット分）- ページ数ベース
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      const limitError = getLimitErrorMessage(totalPages, limitInfo);

      if (isValid && limitError) {
        // yupバリデーションは通ったが上限超過の場合
        setError('images', { type: 'manual', message: limitError });
      }
      // yupバリデーションエラーがある場合はtriggerが既にエラーを設定済み
    }, 0);
  };

  // 日時フォーマット
  const formatTimestamp = (timestamp: unknown): string => {
    if (!timestamp) return '-';
    const date =
      timestamp instanceof Date ? timestamp : (timestamp as { toDate: () => Date }).toDate();
    return new Date(date).toLocaleString('ja-JP', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  // 結果行クリック処理
  const handleResultClick = (resultId: string, patternId: string): void => {
    // patternIdとresultIdが両方存在する場合のみ詳細ページへ遷移
    if (patternId && resultId) {
      // クリック可能な最近の処理履歴リストを構築（次へ/前へナビゲーション用）
      const recentResultNavList: RecentResultNavItem[] = recentResults
        .filter(result => result.ocr_status === ocrStatus.completed && result.patternId)
        .map(result => ({
          id: result.id as string,
          patternId: result.patternId as string,
        }));

      // フリーOCRの結果は専用画面へ遷移する（#2858）。
      // patterns に実在しない 'free_ocr' を URL に含めると通常パターン用画面で表示できないため。
      if (patternId === FREE_OCR_PATTERN_ID) {
        navigate(`/ai-ocr/free/result/${resultId}`, {
          state: { recentResults: recentResultNavList },
        });
        return;
      }

      navigate(`/ai-ocr/pattern/${patternId}/result/${resultId}`, {
        state: { recentResults: recentResultNavList },
      });
    }
  };

  // 仕分け変更ボタンクリック処理
  const handleReclassifyClick = (
    e: React.MouseEvent,
    resultId: string,
    currentPatternId: string,
  ): void => {
    e.stopPropagation();
    setReclassifyTargetResultId(resultId);
    setReclassifyTargetPatternId(currentPatternId);
    setReclassifyModalOpen(true);
  };

  // 仕分け変更のクレジットチェック
  const handleReclassifySelect = (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): void => {
    // 代理モード・パターンなし選択時はチェック不要
    if (!isProxyMode && newPatternId !== PATTERN_ID_OTHER && company) {
      const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);
      if (limitInfo.totalRemainingCount <= 0) {
        setReclassifyModalOpen(false);
        setLimitDialogOpen(true);
        return;
      }
    }
    void executeReclassify(newPatternId, itemList);
  };

  // 仕分け変更実行処理
  const executeReclassify = async (
    newPatternId: string,
    itemList: Record<string, unknown>[],
  ): Promise<void> => {
    setReclassifyModalOpen(false);
    setReclassifyLoading(true);

    // モーダルを閉じた直後にUIを即時更新（「パターンなし」はエラー、それ以外は読取中）
    setRecentResults(prev =>
      prev.map(r =>
        r.id === reclassifyTargetResultId
          ? {
              ...r,
              ocr_status:
                newPatternId === PATTERN_ID_OTHER ? ocrStatus.error : ocrStatus.processing,
            }
          : r,
      ),
    );

    let feProvisionalCreditAmount = 0;
    let provisionalDeducted = false;
    // FE仮加算（current_month_ocr_cost）に +15円 が乗ったか。BEの確定 delta 計算に渡す（#2933）。
    // これを渡し忘れると、確定で「仮加算にマスタ +15円 が無い」前提になりマスタ分が二重計上される。
    let feProvisionalHasMasterMapping = false;

    // 該当レコードのページ数を取得
    const targetResult = recentResults.find(r => r.id === reclassifyTargetResultId);
    const targetPageCount = targetResult?.originalFiles?.length || targetResult?.files?.length || 1;

    // 仕分け先パターンのgemini_modelを取得（Pro単価の仮引き・OCR実行に使用）
    const reclassifyPattern =
      newPatternId !== PATTERN_ID_OTHER ? await getPattern(newPatternId).catch(() => null) : null;
    const reclassifyGeminiModel = reclassifyPattern?.gemini_model ?? GEMINI_MODEL_DEFAULT;
    const reclassifyHasMasterMapping = reclassifyPattern?.has_master_mapping === true;

    try {
      // パターンなし選択時はOCR不要なので仮消費しない
      if (newPatternId !== PATTERN_ID_OTHER && company) {
        // 仮消費実行（ページ数分 + マスタ照合が有効なら +15円）
        const provisionalResult = await executeProvisionalOcrUsage(
          company,
          targetPageCount,
          baseRemainingCount,
          isProxyMode,
          reclassifyGeminiModel,
          reclassifyHasMasterMapping,
          1,
          [],
          baseRemainingJpy,
        );
        feProvisionalCreditAmount = provisionalResult.feProvisionalCreditAmount;
        feProvisionalHasMasterMapping = provisionalResult.provisionalHasMasterMapping;
        provisionalDeducted = true;
      }

      // フロントからpattern_idを即座に変更（onSnapshotで自動的にリスト反映）
      const statusData =
        newPatternId === PATTERN_ID_OTHER
          ? { pattern_id: newPatternId, ocr_status: ocrStatus.error }
          : { pattern_id: newPatternId, ocr_status: ocrStatus.processing };
      await editResult({
        resultId: reclassifyTargetResultId,
        data: statusData,
        userId: user?.uid,
      });

      setInfoText('仕分けを変更しました');
      setSnackbarOpen(true);
      setReclassifyLoading(false);

      // バックグラウンドでOCR再読取を実行（完了を待たない）
      void handleReOcrPatternResult({
        resultId: reclassifyTargetResultId,
        patternId: newPatternId,
        itemList,
        isReclassification: true,
        provisionalDeducted,
        feProvisionalCreditAmount,
        feProvisionalUsageCount: targetPageCount,
        provisionalHasMasterMapping: feProvisionalHasMasterMapping,
        isProxy: isProxyMode,
        operatorId: isProxyMode ? operatorId || undefined : undefined,
        geminiModel: reclassifyGeminiModel,
      });
    } catch (error) {
      const message = convertToUserFriendlyMessage(error);
      setErrorText(message);
      setSnackbarOpen(true);
      setReclassifyLoading(false);
    }
  };

  // チュートリアル関連のハンドラー
  const handleRegisterPatternClick = (): void => {
    navigate('/pattern/add');
  };

  const handleTutorialModalOpen = (): void => {
    setTutorialModalOpen(true);
  };

  const handleTutorialModalClose = (): void => {
    setTutorialModalOpen(false);
  };

  const handleTutorialComplete = (): void => {
    setTutorialModalOpen(false);
    navigate('/pattern/add');
  };

  const handleVideoClick = (): void => {
    window.open(TUTORIAL_VIDEO_URL, '_blank', 'noopener,noreferrer');
  };

  const handleFaqViewAll = (): void => {
    // よくある質問一覧ページへ遷移
    window.open(HELP_BASE_URL, '_blank', 'noopener,noreferrer');
  };

  // よくある質問のデータ
  const faqList = [
    {
      question: '項目の設定のコツを教えて',
      link: HELP_FAQ_ITEM_SETTING_TIPS,
    },
    {
      question: 'レイアウトの違いや、表記ゆれを読み取るコツを教えて',
      link: HELP_FAQ_LAYOUT_NOTATION,
    },
    {
      question: '操作手順を教えて',
      link: HELP_FAQ_OPERATION_STEPS,
    },
  ];

  if (isLoading) {
    return (
      <Box sx={layoutStyles.pageWrapper}>
        <MetaTitle customTitle="ホーム" />
        <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
          <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
        </Box>
        <Box sx={[layoutStyles.pageContent, sideBarCollapsed && layoutStyles.pageContentCollapsed]}>
          <CustomLoading loading={true} />
        </Box>
      </Box>
    );
  }

  if (!user) {
    return <></>;
  }

  // 上限超過フラグとメッセージの計算（ボタン非活性用）
  // 代理モード中も月間枠・OCRクレジット消費メッセージは意図的にそのまま表示する。
  // 運営スタッフが対象企業ユーザーと同じ視点で操作できるようにするため。
  // 実際には代理モード中のOCR実行は current_month_ocr_count / ocr_credit_balance を消費しない（表示と実消費の差は仕様として許容）。
  const { isOverLimit, fileCountMessage } = ((): {
    isOverLimit: boolean;
    fileCountMessage: string;
  } => {
    // 合計ページ数の上限チェック
    if (totalPageCount > MAX_TOTAL_PAGES) {
      return {
        isOverLimit: true,
        fileCountMessage: `合計ページ数が上限を超えています。（${totalPageCount}ページ / 上限${MAX_TOTAL_PAGES}ページ）`,
      };
    }
    // 個人別 月間OCR上限（#3133）。会社枠より先に判定する。
    const userLimitMessage = getUserLimitMessage(totalPageCount);
    if (userLimitMessage) {
      return { isOverLimit: true, fileCountMessage: userLimitMessage };
    }
    if (baseRemainingCount === null || !company) {
      return { isOverLimit: false, fileCountMessage: '' };
    }
    const limitInfo = calculateOcrLimitInfo(company, baseRemainingCount);

    // 合計枚数を超えた場合はエラー
    const overMessage = getOcrLimitOverMessage(totalPageCount, limitInfo);
    if (overMessage) {
      return { isOverLimit: true, fileCountMessage: overMessage };
    }
    // 月間枠は超えているがOCRクレジット内の場合は情報メッセージ
    const creditMessage = getOcrCreditUsageMessage(totalPageCount, limitInfo);
    if (creditMessage) {
      return { isOverLimit: false, fileCountMessage: creditMessage };
    }
    return { isOverLimit: false, fileCountMessage: '' };
  })();

  return (
    <Box sx={layoutStyles.pageWrapper}>
      <MetaTitle customTitle="ホーム" />
      <Box sx={[layoutStyles.sidebar, sideBarCollapsed && layoutStyles.sidebarCollapsed]}>
        <SideBar collapsed={sideBarCollapsed} onToggle={toggleSidebar} />
      </Box>

      <Box
        sx={[
          layoutStyles.pageContent,
          sideBarCollapsed && layoutStyles.pageContentCollapsed,
          {
            '@media screen and (max-width:960px)': {
              paddingBottom: 0,
            },
          },
        ]}
      >
        <Box sx={layoutStyles.header}>
          <Typography
            sx={{
              ...boldXxl,
              color: gray100,
              '@media screen and (max-width:960px)': { ...spHeaderTitle },
            }}
          >
            ホーム
          </Typography>
        </Box>

        {/* コンテンツエリア全体にマージンを追加 */}
        <Box
          sx={{
            width: '100%',
            boxSizing: 'border-box',
            paddingLeft: '32px',
            paddingRight: '32px',
            '@media screen and (max-width:960px)': {
              paddingLeft: '16px',
              paddingRight: '16px',
            },
          }}
        >
          {/* チュートリアルカード または ファイルアップロードセクション */}
          {showTutorial === null ? null : showTutorial ? (
            <TutorialCard
              onRegisterClick={handleRegisterPatternClick}
              onTutorialClick={handleTutorialModalOpen}
              onVideoClick={handleVideoClick}
            />
          ) : (
            <Box sx={styles.container}>
              {/* タイトルと説明、右上にリンク */}
              <Box
                sx={{
                  display: 'flex',
                  justifyContent: 'space-between',
                  alignItems: 'flex-start',
                  marginBottom: '16px',
                  '@media screen and (max-width: 600px)': {
                    flexDirection: 'column',
                    gap: '8px',
                  },
                }}
              >
                <Box>
                  <Typography sx={styles.subSectionTitle}>自動仕分</Typography>
                  <Typography
                    variant="body2"
                    sx={{
                      marginTop: '8px',
                      color: gray100,
                      textAlign: 'left',
                      fontSize: '16px',
                      lineHeight: '1.5',
                    }}
                  >
                    AIが書類を自動で仕分けて、種類ごと（請求書・納品書など）に読取します。
                  </Typography>
                </Box>
                <Link
                  component="button"
                  onClick={(): void => {
                    window.open(HELP_FAQ_AUTO_CLASSIFICATION, '_blank', 'noopener,noreferrer');
                  }}
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    fontSize: '14px',
                    fontWeight: 700,
                    color: primaryButtonBackgroundColor.backgroundColor,
                    cursor: 'pointer',
                    textDecoration: 'none',
                    whiteSpace: 'nowrap',
                    '&:hover': {
                      textDecoration: 'underline',
                    },
                  }}
                >
                  <HelpOutlineOutlined sx={{ fontSize: '16px' }} />
                  {AUTO_CLASSIFY_HELP_LABEL}
                </Link>
              </Box>

              {/* ファイルアップローダー */}
              <Controller
                name="images"
                control={control}
                render={({ field }): ReactElement => (
                  <FileUploader
                    onFilesSelected={(files: FileList | null): void => {
                      void handleFilesSelected(files, field.onChange);
                    }}
                    onButtonClick={handleFileUploadButtonClick}
                    helperText={
                      errors.images?.message || (isOverLimit ? fileCountMessage : undefined)
                    }
                    infoText={
                      !isOverLimit && !errors.images ? fileCountMessage || undefined : undefined
                    }
                    showNoteInside={true}
                    noteText={`※PDF、PNG、JPG、JPEGファイルを選択できます。最大${getDisplayUploadLimit(company)}ファイル・各10MBまで。\n合計ページ数は${MAX_TOTAL_PAGES}ページまで可能です。\n複数ページをまとめて出力する場合、PDFは1ファイルあたり${MAX_PAGES_PER_FILE}ページまで可能です。`}
                  />
                )}
              />

              {/* 選択済みファイル一覧表示 */}
              {watch('images') && watch('images').length > 0 && (
                <Box sx={{ marginTop: '12px' }}>
                  <TableContainer sx={styles.tableContainer}>
                    <Table>
                      <TableBody>
                        {watch('images').flatMap(
                          (file: File, index: number): React.ReactElement[] => {
                            const nameWithoutExt = file.name.replace(/\.[^/.]+$/, '');
                            const ext = file.name.match(/\.[^/.]+$/)?.[0] ?? '';
                            const pageCount =
                              processingTypeValue === fileProcessingType.pagePerResult &&
                              isPdfFile(file)
                                ? (pdfPageCountCache.get(file.name) ?? 1)
                                : 1;
                            return Array.from({ length: pageCount }, (_, pageIndex) => {
                              const displayName =
                                pageCount > 1
                                  ? `${nameWithoutExt}_${pageIndex + 1}${ext}`
                                  : file.name;
                              return (
                                <TableRow key={`${index}-${pageIndex}`} sx={styles.TableRow}>
                                  <TableCell sx={styles.TableCell}>
                                    <Button
                                      sx={{
                                        textAlign: 'left',
                                        padding: 0,
                                        minWidth: 'auto',
                                        color: '#1976d2',
                                        textTransform: 'none',
                                        '&:hover': {
                                          textDecoration: 'underline',
                                          backgroundColor: 'transparent',
                                        },
                                      }}
                                    >
                                      {displayName}
                                    </Button>
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...styles.TableCell,
                                      color: gray80,
                                      textAlign: 'right',
                                      paddingRight: '4px',
                                      width: '80px',
                                      minWidth: '80px',
                                      maxWidth: '80px',
                                    }}
                                  >
                                    {formatFileSize(file.size)}
                                  </TableCell>
                                  <TableCell
                                    sx={{
                                      ...styles.TableCell,
                                      textAlign: 'end',
                                      width: '32px',
                                      minWidth: '32px',
                                      maxWidth: '32px',
                                      padding: '8px 8px 8px 0',
                                    }}
                                  >
                                    {pageIndex === 0 && (
                                      <Button
                                        sx={styles.cancelButton}
                                        onClick={(): void => {
                                          void handleFilesDeleted(index);
                                        }}
                                      >
                                        <CancelIcon />
                                      </Button>
                                    )}
                                  </TableCell>
                                </TableRow>
                              );
                            });
                          },
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </Box>
              )}

              {/* 出力方式選択 */}
              <OutputTypeSelector
                value={processingTypeValue}
                onChange={(value: number): void => {
                  setProcessingTypeValue(value);
                  // ファイル処理方式変更時にPDF分割/復元とバリデーションを実行
                  const currentFiles = watch('images');
                  if (currentFiles && currentFiles.length > 0) {
                    void handleProcessingTypeChange(value, currentFiles);
                  }
                }}
                showDescription={false}
              />

              {/* アップロードボタン（ファイル選択時のみ表示） */}
              {watch('images') && watch('images').length > 0 && (
                <Button
                  sx={{ ...styles.uploadSubmitButton, marginTop: '8px' }}
                  onClick={handleSubmit(onSubmit)}
                  disabled={!!errors.images || isOverLimit || isCalculating}
                  data-gtm-label="home_start_ocr_settings"
                >
                  <Typography sx={styles.uploadSubmitButtonText}>
                    書類を自動仕分してAI読取
                  </Typography>
                </Button>
              )}
            </Box>
          )}

          <Box sx={styles.mainContainer}>
            {/* 左カラム */}
            <Box sx={styles.leftColumn}>
              {/* 最近の処理履歴 */}
              <Box sx={[styles.container, { padding: '16px', flex: 1 }]}>
                {/* ヘッダー部分 */}
                <Box
                  sx={{
                    display: 'flex',
                    justifyContent: 'space-between',
                    alignItems: 'center',
                    marginBottom: '16px',
                  }}
                >
                  <Typography
                    sx={{
                      ...styles.subSectionTitle,
                      marginBottom: 0,
                      fontSize: '16px',
                      fontWeight: 700,
                    }}
                  >
                    最近の処理履歴
                  </Typography>
                </Box>

                {recentResults.length === 0 ? (
                  /* 空の状態 */
                  <Box
                    sx={{
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'center',
                      justifyContent: 'center',
                      padding: '24px 16px',
                      gap: '12px',
                    }}
                  >
                    <Box
                      sx={{
                        width: '48px',
                        height: '48px',
                        backgroundColor: white,
                        borderRadius: '8px',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Description sx={{ fontSize: '40px', color: gray60 }} />
                    </Box>
                    <Box sx={{ textAlign: 'center' }}>
                      <Typography sx={{ fontSize: '14px', color: gray80, marginBottom: '4px' }}>
                        まだ処理履歴がありません。
                      </Typography>
                      <Typography sx={{ fontSize: '14px', color: gray80 }}>
                        書類をアップロードすると、ここに読み取り結果が表示されます。
                      </Typography>
                    </Box>
                  </Box>
                ) : (
                  /* データがある場合 */
                  <Box sx={styles.listWrapper}>
                    {recentResults.map((result, index): React.ReactElement => {
                      // 完了かつpatternIdがある場合のみクリック可能
                      const isDisabled =
                        result.ocr_status !== ocrStatus.completed || !result.patternId;

                      // ステータステキストの取得
                      const getStatusText = (): string => {
                        if (result.ocr_status === ocrStatus.error) {
                          return result.patternId === PATTERN_ID_OTHER
                            ? '該当するパターンがありません'
                            : 'ファイルのアップロードに失敗しました';
                        }
                        if (result.ocr_status === ocrStatus.completed) {
                          return '読取完了';
                        }
                        if (
                          result.ocr_status === ocrStatus.uploaded ||
                          result.ocr_status === ocrStatus.processing
                        ) {
                          return '読取中....';
                        }
                        return '仕分け中';
                      };

                      return (
                        <Box
                          key={index}
                          sx={{
                            ...styles.listItem,
                            cursor: isDisabled ? 'default' : 'pointer',
                            '&:hover': isDisabled
                              ? {}
                              : {
                                  backgroundColor: gray20,
                                },
                          }}
                          onClick={(): void => {
                            if (!isDisabled) {
                              handleResultClick(result.id, result.patternId);
                            }
                          }}
                        >
                          {/* ステータスアイコン */}
                          <Box sx={styles.listItemIconContainer}>
                            <OcrStatusIcon status={result.ocr_status} />
                          </Box>

                          {/* マージンを含むコンテンツ領域 */}
                          <Box sx={styles.listItemMargin}>
                            {/* ファイル情報（ファイル名＋ステータスの2行表示） */}
                            <OcrFileNameStatus
                              fileName={result.originalFiles?.[0]?.file_name || '-'}
                              statusText={getStatusText()}
                              /* 要注意をOCR一覧と同じバッジ・同じ位置で示す（#3330）。
                                 件数は出さない（#3328）。review_item_count は保存時点の件数で、
                                 詳細画面の判定（画面を開いた時点）とズレうるため */
                              fileNameSuffix={
                                isNeedsReviewResult(result) ? <ReviewChip /> : undefined
                              }
                              // どの子パターンで読んだかをOCR一覧と同じバッジで示す（#3314）
                              statusSuffix={
                                <SubPatternChip subPatternName={result.sub_pattern_name} />
                              }
                            />

                            {/* 仕分け種類バッジ */}
                            <Box sx={styles.listItemBadgeContainer}>
                              {!result.patternId ? (
                                <Typography sx={styles.listItemBadgeTextOnly}>
                                  仕分け中...
                                </Typography>
                              ) : result.patternId === PATTERN_ID_OTHER ? (
                                <Box sx={styles.listItemBadge}>
                                  <Typography sx={styles.listItemBadgeText}>
                                    パターンなし
                                  </Typography>
                                </Box>
                              ) : result.patternId === FREE_OCR_PATTERN_ID ? (
                                <Box sx={styles.listItemBadge}>
                                  <Typography sx={styles.listItemBadgeText}>フリーOCR</Typography>
                                </Box>
                              ) : (
                                <Box sx={styles.listItemBadge}>
                                  <Typography sx={styles.listItemBadgeText}>
                                    {result.pattern_name || '-'}
                                  </Typography>
                                </Box>
                              )}
                              {/* 仕分け変更ボタン（完了・エラー状態のみ表示） */}
                              {(result.ocr_status === ocrStatus.completed ||
                                result.ocr_status === ocrStatus.error) &&
                                result.patternId && (
                                  <Typography
                                    component="span"
                                    sx={{
                                      fontSize: '12px',
                                      color: reclassifyLoading ? gray40 : gray80,
                                      cursor: reclassifyLoading ? 'default' : 'pointer',
                                      pointerEvents: reclassifyLoading ? 'none' : 'auto',
                                      display: 'inline-flex',
                                      alignItems: 'center',
                                      marginTop: '4px',
                                      '&:hover': {
                                        textDecoration: reclassifyLoading ? 'none' : 'underline',
                                      },
                                    }}
                                    onClick={(e: React.MouseEvent<HTMLSpanElement>): void =>
                                      handleReclassifyClick(e, result.id, result.patternId)
                                    }
                                  >
                                    <SwapHoriz sx={{ fontSize: '14px', marginRight: '2px' }} />
                                    仕分け変更
                                  </Typography>
                                )}
                            </Box>

                            {/* 日時 */}
                            <Typography sx={styles.listItemDateTime}>
                              {formatTimestamp(result.created_at)}
                            </Typography>
                          </Box>

                          {/* 矢印アイコン */}
                          <NavigateNext sx={styles.listItemArrow} />
                        </Box>
                      );
                    })}
                  </Box>
                )}
              </Box>
            </Box>

            {/* 右カラム（利用状況・招待・よくある質問を縦並び） */}
            <Box sx={styles.rightColumn}>
              {/* 今月の利用状況 */}
              <UsageStatusCard processedCount={monthlyUsageCount} />

              {/* 招待リンク生成（運営はボタンdisabled。代理モード時は手本として通常表示） */}
              <ReferralInvitationCard
                disabled={isOperatorStaff(firestoreUser?.role) && !isProxyMode}
              />

              {/* よくある質問 */}
              <FaqCard faqList={faqList} onViewAllClick={handleFaqViewAll} />
            </Box>
          </Box>
        </Box>

        {/* チュートリアルモーダル */}
        <TutorialModal
          open={tutorialModalOpen}
          onClose={handleTutorialModalClose}
          onComplete={handleTutorialComplete}
        />

        {/* ローディング（タイムアウト時は強制解除してユーザーが操作できるようにする） */}
        <CustomLoading loading={(dataLoading || showTutorial === null) && !loadingTimedOut} />

        {/* 削除確認モーダル */}
        <DeleteModal
          open={deleteModalOpen}
          onClose={(): void => setDeleteModalOpen(false)}
          onDelete={handleDeleteResult}
          title="ファイル"
        />

        {/* OCR上限ダイアログ */}
        <OcrLimitDialog
          open={limitDialogOpen}
          onClose={(): void => setLimitDialogOpen(false)}
          companyPlan={company?.plan}
        />

        {/* 仕分け変更モーダル */}
        <PatternSelectModal
          open={reclassifyModalOpen}
          onClose={(): void => setReclassifyModalOpen(false)}
          onSelect={handleReclassifySelect}
          currentPatternId={reclassifyTargetPatternId}
          companyId={effectiveCompanyId || ''}
          usesCredit={calculateOcrLimitInfo(company, baseRemainingCount).remainingCount <= 0}
          hideWarning={isProxyMode}
        />

        {/* スナックバー */}
        <CustomSnackbar
          open={snackbarOpen}
          onClose={(): void => setSnackbarOpen(false)}
          text={errorText}
          infoText={infoText}
          closeButton={(): void => setSnackbarOpen(false)}
        />
      </Box>
    </Box>
  );
};

export default Home;
